/**
 * krokiService.js — Kroki (salon taban planı) sayfalarının servis dosyası
 *
 * Sayfalar: /Management/Kroki (KrokiPage.jsx) ve /fuar/salon/:salonId (SalonKrokiPage.jsx)
 *
 * DİKKAT — bu servis çoğunlukla CANLIDIR (USE_MOCK_DATA = false). Kroki motoru
 * henüz gelmedi ama salon/stand/fuar alanı verisinin tamamı gerçek Java gateway
 * uçlarından okunuyor; sayfadaki "boş çerçeveler" sadece kroki çiziminin kendisi
 * için ayrılmış alanlar, veri için değil.
 *
 * Kullanılan gerçek uçlar (2026-08-16'da yerel backend'e karşı doğrulandı):
 *   GET /api/v1/fuar/{fuarId}
 *       → { id, fuarName, fuarAlaniEntity:{id,fuarAlaniName}, fuarSalons:[{id, salon_name}] }
 *       Not: alan adı snake_case `salon_name` (camelCase DEĞİL), bkz. normalizeSalon().
 *       Doğrulandı: fuar 295 → 14 salon, fuar 298 → 1 salon (94 "Mock Salon A (mock)").
 *   GET /api/v1/standTalep/list?fuarId=&salonId=&page=&size=
 *       → { data:[{standId, standName, salonId, salonName, katilimciFirmaName, ...}],
 *           totalCount, totalPages, page, size, first, last }
 *       bkz. docs/guides/FE-StandTalepController-guide.md
 *   GET /api/v1/fuar/listFuarAlani  → [{ id, fuarAlaniName }]
 *       Gerçek login gerektirir (oturumsuz 401) — FuarController'da auth bypass YOK,
 *       bkz. docs/guides/FE-README-guide.md "Controllers still requiring a real login".
 *
 * "Fuar alanına göre salon listesi" (backlog #51, 2026-09-20): WAS mock — the endpoint this
 *   file's own TODO was waiting on already existed under a different name the whole time:
 *   GET /fuar/alani/{id}/salonlar, wired up in fuarAlaniSalonService.listSalonlar for the
 *   FairAreas module. getSalonlarByFuarAlani now delegates to it instead of the stale
 *   /fuaralani/{id}/salons guess (never a real route) or the mock table.
 */

import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { listSalonlar as listFuarAlaniSalonlar } from './fuarAlaniSalonService';
import i18n from "../i18n";

const FUAR_API = `${JAVA_BACKEND_API_URL}/fuar`;
const STAND_TALEP_API = `${JAVA_BACKEND_API_URL}/standTalep`;

// ─── MOCK MODU ────────────────────────────────────────────────────────────────
// Servisin geneli canlı. Aşağıdaki tek bayrak, backend'de karşılığı olmayan
// "fuar alanına göre salonlar" ucunu temsil eder.
export const USE_MOCK_DATA = false;

// Backlog #51 (2026-09-20): the real endpoint exists (see file header) — no longer mock.
// Kept as a named export since KrokiPage.jsx still reads it to decide whether to show the
// "örnek veri" badge; it simply never fires true anymore.
export const SALONLAR_BY_FUAR_ALANI_MOCK = false;

const mockDelay = (ms = 150) => new Promise((r) => setTimeout(r, ms));

// Fuar alanı → salon örnek verisi. Gerçek uç gelene kadar Kroki Çizim
// sekmesinin salon seçicisini besler. Gerçek `salon` tablosundaki isimlendirmeyi
// taklit eder (TÜYAP salonları düz numara, fuayeler isimli).
// Backlog #51 (2026-09-20): no longer read anywhere — getSalonlarByFuarAlani is live now.
// Kept, not deleted.
// const _mockSalonlarByFuarAlani = {
//   1: [
//     { salonId: 9401, salonName: '1', salonCode: 'H1' },
//     { salonId: 9402, salonName: '2', salonCode: 'H2' },
//     { salonId: 9403, salonName: '3', salonCode: 'H3' },
//     { salonId: 9404, salonName: '4', salonCode: 'H4' },
//     { salonId: 9405, salonName: 'ANA FUAYE', salonCode: 'AF' },
//   ],
//   9: [
//     { salonId: 9501, salonName: '1', salonCode: 'H1' },
//     { salonId: 9502, salonName: '2', salonCode: 'H2' },
//     { salonId: 9503, salonName: '3', salonCode: 'H3' },
//     { salonId: 9504, salonName: '11a', salonCode: 'H11A' },
//     { salonId: 9505, salonName: 'ANA FUAYE', salonCode: 'AF' },
//   ],
// };

// listFuarAlani gerçek login ister; oturum düşerse sekme tamamen ölmesin diye
// yedek liste. Sayfa bu yedeğe düştüğünde kullanıcıya açıkça rozet gösterir
// (getFuarAlanlari() dönüşündeki `isFallback`).
const _mockFuarAlanlari = [
  { id: 1, fuarAlaniName: 'TÜYAP' },
  { id: 9, fuarAlaniName: 'TÜYAP 2023' },
];

// Salon sayısı bu eşiği aşan fuarlarda tüm stand listesini çekip gruplamak
// yerine salon başına tek satırlık sayım isteği atılır (bkz. getSalonlarByFuar).
const STAND_GROUPING_CAP = 1500;

// ─── Yardımcılar ──────────────────────────────────────────────────────────────

// /fuar/{id} yanıtındaki salon nesnesi snake_case (`salon_name`) geliyor;
// bazı uçlar camelCase döndürdüğü için ikisini de karşılıyoruz.
function normalizeSalon(s) {
  return {
    salonId: s.id ?? s.salonId ?? null,
    salonName: s.salon_name ?? s.salonName ?? (s.id != null ? `Salon ${s.id}` : '—'),
  };
}

async function fetchStandPage(fuarId, { salonId, page = 0, size = 1 } = {}) {
  const params = new URLSearchParams({ fuarId: String(fuarId), page: String(page), size: String(size) });
  if (salonId != null && salonId !== '') params.set('salonId', String(salonId));
  const res = await axios.get(`${STAND_TALEP_API}/list?${params}`, { headers: getAuthHeader() });
  return res.data || {};
}

// ─── Servis ───────────────────────────────────────────────────────────────────

/**
 * Seçili fuarın salonları + her salondaki stand sayısı.
 * GET /fuar/{fuarId} (salonlar) + GET /standTalep/list (stand sayımı)
 *
 * Dönüş: { salonlar: [{salonId, salonName, standCount, isUnlisted}], toplamStand, sayimEksik }
 *
 * `isUnlisted`: standları olduğu halde fuarın `fuarSalons` listesinde görünmeyen
 * salon. Yerel veride gerçekten oluyor (fuar 298'in fuarSalons'u yalnız salon 94
 * derken standları salon 1/2'de duruyor), o yüzden bu salonlar da listeye
 * ekleniyor — aksi halde sayfa boş/yanıltıcı görünürdü.
 */
export async function getSalonlarByFuar(fuarId) {
  if (!fuarId) return { salonlar: [], toplamStand: 0, sayimEksik: false };
  try {
    const [fuarRes, ilkSayfa] = await Promise.all([
      axios.get(`${FUAR_API}/${fuarId}`, { headers: getAuthHeader() }),
      fetchStandPage(fuarId, { size: 1 }).catch(() => ({ totalCount: 0 })),
    ]);

    const fuar = fuarRes.data || {};
    const listelenen = (fuar.fuarSalons || []).map(normalizeSalon).filter((s) => s.salonId != null);
    const toplamStand = Number(ilkSayfa.totalCount || 0);

    const sayim = new Map();      // salonId → stand sayısı
    const kesfedilen = new Map(); // fuarSalons'ta olmayan ama standı olan salonlar
    let sayimEksik = false;

    if (toplamStand > 0 && toplamStand <= STAND_GROUPING_CAP) {
      // Tek istekte tüm standları çekip grupla — hem sayım hem keşif verir.
      const tumu = await fetchStandPage(fuarId, { size: toplamStand });
      (tumu.data || []).forEach((st) => {
        if (st.salonId == null) return;
        sayim.set(st.salonId, (sayim.get(st.salonId) || 0) + 1);
        if (!kesfedilen.has(st.salonId)) {
          kesfedilen.set(st.salonId, st.salonName || `Salon ${st.salonId}`);
        }
      });
    } else if (toplamStand > STAND_GROUPING_CAP) {
      // Çok büyük fuar: salon başına tek satırlık sayım isteği (keşif yok).
      sayimEksik = true;
      const sayimlar = await Promise.all(
        listelenen.map((s) =>
          fetchStandPage(fuarId, { salonId: s.salonId, size: 1 })
            .then((r) => [s.salonId, Number(r.totalCount || 0)])
            .catch(() => [s.salonId, null]),
        ),
      );
      sayimlar.forEach(([id, c]) => { if (c != null) sayim.set(id, c); });
    }

    const salonlar = listelenen.map((s) => ({
      ...s,
      standCount: sayim.get(s.salonId) ?? null,
      isUnlisted: false,
    }));

    const listelenenIdler = new Set(listelenen.map((s) => s.salonId));
    kesfedilen.forEach((ad, id) => {
      if (!listelenenIdler.has(id)) {
        salonlar.push({ salonId: id, salonName: ad, standCount: sayim.get(id) ?? 0, isUnlisted: true });
      }
    });

    salonlar.sort((a, b) => {
      // Sayısal salon adları doğal sırada ("2" < "11"), metinsel olanlar sona.
      const an = Number(a.salonName), bn = Number(b.salonName);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
      if (!Number.isNaN(an)) return -1;
      if (!Number.isNaN(bn)) return 1;
      return String(a.salonName).localeCompare(String(b.salonName), 'tr');
    });

    return {
      salonlar,
      toplamStand,
      sayimEksik,
      fuarName: fuar.fuarName || null,
      fuarAlaniName: fuar.fuarAlaniEntity?.fuarAlaniName || null,
      fuarAlaniId: fuar.fuarAlaniEntity?.id ?? null,
    };
  } catch (err) {
    console.error('getSalonlarByFuar error:', err);
    throw err;
  }
}

/**
 * Tek salonun künyesi + o salondaki standlar (/fuar/salon/:salonId sayfası).
 * Salon adı fuarın salon listesinden, olmazsa ilk standın salonName'inden çözülür.
 */
export async function getSalonDetay(salonId, fuarId, { page = 0, size = 200 } = {}) {
  if (!salonId) throw new Error(i18n.t("services:krokiService.salonIdZorunlu"));
  try {
    const [fuarRes, standRes] = await Promise.all([
      fuarId
        ? axios.get(`${FUAR_API}/${fuarId}`, { headers: getAuthHeader() }).then((r) => r.data).catch(() => null)
        : Promise.resolve(null),
      fuarId
        ? fetchStandPage(fuarId, { salonId, page, size }).catch(() => ({ data: [], totalCount: 0 }))
        : Promise.resolve({ data: [], totalCount: 0 }),
    ]);

    const standlar = standRes.data || [];
    const fuarSalon = (fuarRes?.fuarSalons || []).map(normalizeSalon)
      .find((s) => String(s.salonId) === String(salonId));

    return {
      salonId: Number(salonId),
      salonName: fuarSalon?.salonName || standlar[0]?.salonName || null,
      isUnlisted: !fuarSalon && standlar.length > 0,
      fuarId: fuarId ? Number(fuarId) : null,
      fuarName: fuarRes?.fuarName || null,
      fuarAlaniName: fuarRes?.fuarAlaniEntity?.fuarAlaniName || null,
      // Gömülü kroki (fdsvector) salonu fuar alanı + salon id çiftiyle açar —
      // bkz. services/krokiVectorService.js.
      fuarAlaniId: fuarRes?.fuarAlaniEntity?.id ?? null,
      standlar,
      standCount: Number(standRes.totalCount || standlar.length || 0),
    };
  } catch (err) {
    console.error('getSalonDetay error:', err);
    throw err;
  }
}

/**
 * Fuar alanları (Kroki Çizim sekmesinin 1. seçicisi).
 * GET /fuar/listFuarAlani — gerçek login ister. Oturum yoksa/uç patlarsa örnek
 * listeye düşer ve `isFallback: true` döner (sayfa bunu rozetle gösterir).
 */
export async function getFuarAlanlari() {
  try {
    const res = await axios.get(`${FUAR_API}/listFuarAlani`, { headers: getAuthHeader() });
    const data = Array.isArray(res.data) ? res.data : [];
    if (!data.length) return { fuarAlanlari: _mockFuarAlanlari, isFallback: true };
    return {
      fuarAlanlari: data.map((a) => ({ id: a.id, fuarAlaniName: a.fuarAlaniName || i18n.t("services:krokiService.fuarAlani", { id: a.id }) })),
      isFallback: false,
    };
  } catch (err) {
    console.error('getFuarAlanlari error (örnek listeye düşülüyor):', err);
    return { fuarAlanlari: _mockFuarAlanlari, isFallback: true };
  }
}

/**
 * Bir fuar alanının salonları — Kroki Çizim sekmesinin 2. seçicisi, ve backlog #51'in
 * Fuar Alanı Detay sayfasındaki Krokiler sekmesi.
 *
 * Backlog #51 (2026-09-20): now delegates to fuarAlaniSalonService.listSalonlar, the FairAreas
 * module's already-live GET /fuar/alani/{id}/salonlar — not mock, and not the
 * /fuaralani/{id}/salons guess below (never a real route; kept commented, not deleted).
 */
export async function getSalonlarByFuarAlani(fuarAlaniId) {
  if (!fuarAlaniId) return [];
  // WAS (until #51): mock table + a guessed /fuaralani/{id}/salons route that 404'd.
  // if (SALONLAR_BY_FUAR_ALANI_MOCK) {
  //   await mockDelay();
  //   return _mockSalonlarByFuarAlani[fuarAlaniId] || _mockSalonlarByFuarAlani[9];
  // }
  // try {
  //   const res = await axios.get(`${JAVA_BACKEND_API_URL}/fuaralani/${fuarAlaniId}/salons`, {
  //     headers: getAuthHeader(),
  //   });
  //   return (res.data || []).map(normalizeSalon);
  // } catch (err) {
  //   console.error('getSalonlarByFuarAlani error:', err);
  //   throw err;
  // }
  try {
    return await listFuarAlaniSalonlar(fuarAlaniId);
  } catch (err) {
    console.error('getSalonlarByFuarAlani error:', err);
    throw err;
  }
}
